/**
 * Die Rechnung hinter den KI-Funktionen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM MILLIONSTEL CENT UND NICHT CENT.
 *
 * Ein einzelner KI-Aufruf kostet Bruchteile eines Cents. Auf ganze Cent gerundet
 * wäre fast jede Zeile 0 — der Verbrauch bliebe für immer bei null und der
 * Deckel griffe nie. Auf 1 Cent aufgerundet wäre es das Zehnfache des Wahren.
 * Also wird fein gezählt und erst bei der Anzeige gerundet.
 *
 * Der Trick, der das ohne Fließkomma möglich macht: der Preis steht als
 * ganzzahliger Cent-Betrag JE MILLION Tokens. Dann ist
 *
 *     Millionstel Cent = Tokens × Preis je Million
 *
 * eine exakte Ganzzahlmultiplikation. Keine Division, keine Rundung, kein Drift.
 *
 * WARUM ZWEI BETRÄGE JE AUFRUF.
 *
 *     einkauf     — was AUVISO bei Anthropic dafür zahlt
 *     verrechnet  — max(Mindestbetrag, einkauf × (1 + Aufschlag/100))
 *
 * Derselbe Rechenweg und dieselben Namen wie im AUVISO CRM (`renderDashKiLive`,
 * `kiMinFor`). Zwei Rechenwege für dieselbe Sache wären zwei Ergebnisse, und der
 * Unterschied fiele erst auf, wenn eine Rechnung bestritten wird.
 *
 * DER INKLUSIVBETRAG ZÄHLT GEGEN DEN VERRECHNETEN BETRAG, nicht gegen den
 * Einkauf. Das ist die Zahl, die der Vermieter sieht, und „3 € inklusive" muss
 * für ihn heißen, was es ihn kostet. Zum Nachrechnen: bei 30 % Aufschlag
 * entsprechen 3,00 € verrechnet rund 2,31 € Einkauf (3,00 / 1,30). Das ist der
 * Betrag, den AUVISO tatsächlich trägt. Es gibt dafür einen Test, damit das
 * niemand versehentlich zurückdreht.
 *
 * DER MINDESTBETRAG WIRKT VOR DEM DECKEL: erst `max(...)`, dann der Vergleich
 * mit dem Inklusivbetrag. Andernfalls wäre der Inklusivbetrag je nach
 * Aufrufgröße unterschiedlich viel wert.
 *
 * Reine Funktionen, kein React, kein Datenzugriff — getestet in aiBudget.test.ts.
 * Dieselben Formeln stehen in Migration 0049 noch einmal in SQL; die Tests hier
 * sind der Beweis, dass beide dasselbe meinen.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Ein Cent in der Feinheit, in der gezählt wird. */
export const MICRO_PER_CENT = 1_000_000

export interface ModelPrice {
  /** Euro-Cent je einer Million Eingabe-Tokens. */
  inputCentPerMTok: number
  /** Euro-Cent je einer Million Ausgabe-Tokens. */
  outputCentPerMTok: number
}

/**
 * Der EINKAUF eines Aufrufs in Millionstel Cent.
 *
 * Exakt: Tokens sind ganzzahlig, Preise sind ganzzahlig, das Produkt ist es auch.
 */
export function purchaseMicroCent(
  inputTokens: number, outputTokens: number, price: ModelPrice,
): number {
  return inputTokens * price.inputCentPerMTok + outputTokens * price.outputCentPerMTok
}

/**
 * Der VERRECHNETE Betrag eines Aufrufs, also Einkauf plus Aufschlag.
 *
 * Der Mindestbetrag steckt hier NICHT drin: er gilt für den Monat, nicht für den
 * einzelnen Aufruf — genauso wie im CRM, wo er auf die Monatssumme wirkt.
 *
 * Sofort auf eine Ganzzahl gerundet, damit sich aus dem Prozentsatz kein
 * Fließkommarest in die Summe schleppt. Der Rundungsfehler liegt bei höchstens
 * einem Millionstel Cent je Aufruf.
 */
export function billedMicroCent(purchaseMicro: number, markupPct: number): number {
  return Math.round(purchaseMicro * (100 + markupPct) / 100)
}

/** Millionstel Cent → Cent, kaufmännisch gerundet. Nur für die Anzeige. */
export function toCent(microCent: number): number {
  return Math.round(microCent / MICRO_PER_CENT)
}

export interface MonthInput {
  /** Summe der verrechneten Beträge aller Aufrufe des Monats, ohne Mindestbetrag. */
  rawBilledMicroCent: number
  /** Wie viele Aufrufe es waren. */
  calls: number
  /** Mindestbetrag der Monatsabrechnung, in Cent. */
  minCent: number
  /** Inklusivbetrag, in Cent. */
  capCent: number
}

export interface MonthState {
  /** Verrechnet im Monat, nach Mindestbetrag, in Millionstel Cent. */
  billedMicroCent: number
  /** Dasselbe in Cent, für die Anzeige. */
  billedCent: number
  /** Davon durch das Abo abgedeckt. */
  includedCent: number
  /** Was darüber hinausgeht und nachverrechnet wird. */
  overageCent: number
  /** 0–1, für den Balken. Über dem Inklusivbetrag bleibt er bei 1. */
  fraction: number
  /** Ob der Inklusivbetrag ausgeschöpft ist. */
  exceeded: boolean
}

/**
 * Der Stand eines Monats.
 *
 * Der Mindestbetrag greift bei NULL Aufrufen nicht: „1,00 € von 3,00 €
 * verbraucht" bei einem Betrieb, der die Funktion nie angerührt hat, wäre
 * schlicht falsch. Er ist ein Mindest-RECHNUNGSbetrag, kein Grundpreis.
 */
export function monthState(m: MonthInput): MonthState {
  const minMicro = m.minCent * MICRO_PER_CENT
  const capMicro = m.capCent * MICRO_PER_CENT
  const billed = m.calls === 0 ? 0 : Math.max(minMicro, m.rawBilledMicroCent)
  return {
    billedMicroCent: billed,
    billedCent: toCent(billed),
    includedCent: Math.min(toCent(billed), m.capCent),
    overageCent: toCent(Math.max(0, billed - capMicro)),
    fraction: capMicro <= 0 ? 1 : Math.min(1, billed / capMicro),
    // Genau auf dem Deckel gilt als ausgeschöpft: der nächste Aufruf kostet.
    exceeded: billed >= capMicro,
  }
}

/**
 * Ob ein weiterer Aufruf noch im Inklusivbetrag liegt.
 *
 * Absichtlich `>=` und nicht `>`: genau auf dem Deckel ist er verbraucht. Ein
 * Betrieb, der auf den Cent genau bei 3,00 € steht, hat seine 3 € bekommen.
 */
export function withinIncluded(billedMicroCent: number, capCent: number): boolean {
  return billedMicroCent < capCent * MICRO_PER_CENT
}

/**
 * Hochrechnung auf das Monatsende, wie im CRM: bisheriger Verbrauch mal
 * Monatstage durch bisherige Tage, danach der Mindestbetrag.
 *
 * Am ersten Tag des Monats ist das eine sehr grobe Zahl — sie multipliziert
 * einen einzigen Tag mit 31. Sie steht trotzdem da, weil der Betrieb, der am
 * 2. schon bei 2,80 € ist, es früh erfahren soll und nicht am 20.
 */
export function projectedMicroCent(
  rawBilledMicroCent: number, dayOfMonth: number, daysInMonth: number, minCent: number,
): number {
  if (rawBilledMicroCent <= 0) return 0
  const day = Math.max(1, dayOfMonth)
  return Math.max(
    minCent * MICRO_PER_CENT,
    Math.round(rawBilledMicroCent * daysInMonth / day),
  )
}

/* ────────────────────────────────────────────────────── Der Monatswechsel */

/** Tage im Monat eines `YYYY-MM-DD`. */
export function daysInMonth(iso: string): number {
  const year = Number(iso.slice(0, 4))
  const month = Number(iso.slice(5, 7))
  // Tag 0 des Folgemonats ist der letzte des laufenden. Bewusst mit `Date.UTC`
  // gerechnet und nur auf die Tageszahl reduziert — hier geht es um Kalender,
  // nicht um Zeitpunkte, und `new Date(iso)` würde über die Sommerzeit rutschen.
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export interface MonthWindow {
  /** Erster Tag des Monats, `YYYY-MM-DD`. */
  from: string
  /** Erster Tag des Folgemonats — halboffen, wie überall in diesem Projekt. */
  until: string
  /** Der wievielte Tag des Monats `iso` ist. */
  day: number
  days: number
}

/**
 * Der Abrechnungsmonat zu einem Datum.
 *
 * Halboffen `[from, until)` wie die Buchungszeiträume: `until` ist der Tag, an
 * dem der Zähler wieder bei null steht, und genau dieses Datum bekommt der
 * Vermieter zu sehen. „Geht nicht" ohne „ab wann wieder" ist eine Sackgasse.
 */
export function monthWindow(iso: string): MonthWindow {
  const year = Number(iso.slice(0, 4))
  const month = Number(iso.slice(5, 7))
  const nextYear = month === 12 ? year + 1 : year
  const nextMonth = month === 12 ? 1 : month + 1
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    from: `${year}-${pad(month)}-01`,
    until: `${nextYear}-${pad(nextMonth)}-01`,
    day: Number(iso.slice(8, 10)),
    days: daysInMonth(iso),
  }
}

/* ──────────────────────────────────────── Was dem CRM gemeldet wird */

export interface UsageEntry {
  /** Einkauf dieses Aufrufs, in Millionstel Cent. */
  purchaseMicroCent: number
  /** Verrechneter Betrag dieses Aufrufs, in Millionstel Cent. */
  billedMicroCent: number
}

/**
 * Der EINKAUFSWERT des Teils, der über dem Inklusivbetrag liegt.
 *
 * ZWEI FEHLER, DIE HIER GELD KOSTEN WÜRDEN:
 *
 *   1. Gemeldet wird der EINKAUF, nicht der Verkaufspreis. Das CRM schlägt seine
 *      Marge selbst auf. Meldete man den bereits aufgeschlagenen Betrag, schlüge
 *      das CRM ein zweites Mal auf und der Kunde zahlte bei 30 % in Summe 69 %.
 *
 *   2. Gemeldet wird nur, was ÜBER dem Inklusivbetrag liegt. Die ersten 3 € sind
 *      mit dem Abo bezahlt; meldete man den ganzen Monat, zahlte der Kunde sie
 *      ein zweites Mal.
 *
 * Der Aufruf, der die Grenze überschreitet, wird ANTEILIG geteilt. Sonst hinge
 * es vom Zufall der Aufrufgröße ab, wie viel wirklich inklusive war: ein Betrieb
 * mit einem großen Aufruf bekäme mehr geschenkt als einer mit zwanzig kleinen,
 * obwohl beide dieselben 3 € haben.
 *
 * Die Aufrufe müssen in ihrer zeitlichen Reihenfolge übergeben werden — dieselbe
 * Reihenfolge, in der `ai_overage_report_data` in 0049 kumuliert.
 */
export function overagePurchaseMicroCent(entries: UsageEntry[], capCent: number): number {
  const capMicro = capCent * MICRO_PER_CENT
  let cum = 0
  let out = 0
  for (const e of entries) {
    const before = cum
    cum += e.billedMicroCent
    if (cum <= capMicro) continue
    if (before >= capMicro) {
      out += e.purchaseMicroCent
    } else if (e.billedMicroCent > 0) {
      out += Math.round(e.purchaseMicroCent * (cum - capMicro) / e.billedMicroCent)
    }
  }
  return out
}

/* ─────────────────────────────────────────────── Die Demo ruft nicht an */

/**
 * DER DEMOMODUS RUFT KEINE KI AUF — und das ist keine Sparmaßnahme.
 *
 * Die Demo ist öffentlich zugänglich, hat keinen Mandanten und damit kein
 * Budget. Ein KI-Aufruf von dort wäre ein offener Hahn auf dem Schlüssel des
 * Betreibers: jeder Besucher, beliebig oft, ohne Zuordnung und ohne Deckel.
 * Serverseitig ist das ohnehin dicht (die `ai`-Function verlangt eine
 * auflösbare Sitzung MIT Mandantenzugehörigkeit und lehnt sonst ab, statt zu
 * raten) — aber die Oberfläche soll es gar nicht erst versuchen.
 *
 * Stattdessen liegt hier ein einmal erzeugtes, mitgeliefertes Ergebnis je
 * Funktion. Es ist echt und es soll gut sein: es ist das, was den Interessenten
 * überzeugt.
 *
 * ES WIRD BESCHRIFTET, NICHT GETARNT. Ein vorbereitetes Ergebnis, das sich als
 * frisch gerechnet ausgibt, ist eine Täuschung des Interessenten — und der
 * erste Eindruck von einem Produkt, dem er später seine Buchhaltung anvertrauen
 * soll. `demoNotice()` gehört sichtbar neben jedes dieser Ergebnisse.
 */
export const DEMO_AI_EXAMPLES: Record<string, string> = {
  text_suggestion:
    'Liebe Familie Berger,\n\nherzlichen Dank für Ihre Anfrage! Für 25. bis 29. August '
    + 'hätten wir die Ferienwohnung „Bergblick" frei — 62 m², Südbalkon mit Blick auf den '
    + 'Dachstein, für vier Personen bequem.\n\nDie vier Nächte kosten 596,00 € inklusive '
    + 'Endreinigung; die Ortstaxe von 2,50 € je Erwachsenem und Nacht kommt vor Ort dazu. '
    + 'Ihr Hund ist herzlich willkommen, wir berechnen dafür 12,00 € pro Nacht.\n\n'
    + 'Wenn Sie möchten, halte ich die Wohnung bis Freitag für Sie fest.\n\n'
    + 'Herzliche Grüße\nMaria Berghofer',
  enquiry_parse:
    'Anreise 25.08.2026 · Abreise 29.08.2026 · 2 Erwachsene, 2 Kinder (6 und 9) · '
    + '1 Hund · Wunsch: Balkon, ruhige Lage · Kontakt: familie.berger@example.at',
  price_advice:
    'Für die Woche vom 8. bis 15. August beträgt Ihr Preis 149,00 € je Nacht; '
    + '80 % Ihrer Einheiten sind bereits belegt. Prüfen Sie für diese Woche 165,00 € '
    + 'je Nacht. Bei sieben verkauften Nächten wären das rechnerisch 112,00 € mehr. '
    + 'Dieser Vorschlag nutzt Ihre eigenen Belegungsdaten, keinen Marktvergleich.',
}

/** Die Beschriftung, die neben jedem Demo-Ergebnis stehen MUSS. */
export function demoNotice(de: boolean): string {
  return de
    ? 'Beispiel aus der Demo. In Ihrem Konto rechnet die KI mit Ihren eigenen Daten.'
    : 'Example from the demo. In your own account the AI works with your own data.'
}
